import React, { useEffect, useState } from 'react';
import { authApi, usersApi } from '../api/client';

// ─── Constants ─────────────────────────────────────────────────────────────────
const ALL_ROLES = [
  'admin',
  'contracting_officer',
  'contracting_specialist',
  'program_manager',
  'cor',
  'market_research_analyst',
  'technical_evaluator',
  'price_evaluator',
  'evaluation_chair',
  'legal_reviewer',
  'small_business_reviewer',
  'security_reviewer',
  'auditor',
  'requester',
];

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  admin: { bg: '#f3e8ff', color: '#7c3aed' },
  contracting_officer: { bg: '#dbeafe', color: '#2563eb' },
  contracting_specialist: { bg: '#e0e7ff', color: '#4f46e5' },
  program_manager: { bg: '#ccfbf1', color: '#0d9488' },
  cor: { bg: '#ccfbf1', color: '#0d9488' },
  market_research_analyst: { bg: '#ccfbf1', color: '#0d9488' },
  technical_evaluator: { bg: '#ffedd5', color: '#c2410c' },
  price_evaluator: { bg: '#ffedd5', color: '#c2410c' },
  evaluation_chair: { bg: '#ffedd5', color: '#c2410c' },
  legal_reviewer: { bg: '#fef9c3', color: '#a16207' },
  small_business_reviewer: { bg: '#fef9c3', color: '#a16207' },
  security_reviewer: { bg: '#fef9c3', color: '#a16207' },
  auditor: { bg: '#fee2e2', color: '#dc2626' },
  requester: { bg: '#f3f4f6', color: '#4b5563' },
};

// ─── Styles ────────────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: 'white',
  borderRadius: 8,
  padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #e5e7eb',
  marginBottom: 20,
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 16px',
  background: '#4f46e5',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '6px 12px',
  background: '#f3f4f6',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  padding: '6px 12px',
  background: '#fef2f2',
  color: '#dc2626',
  border: '1px solid #fecaca',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};

const btnSuccess: React.CSSProperties = {
  padding: '6px 12px',
  background: '#f0fdf4',
  color: '#16a34a',
  border: '1px solid #bbf7d0',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 13,
  width: '100%',
  boxSizing: 'border-box',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #e5e7eb',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 12px',
  fontSize: 13,
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle',
};

// ─── Component ─────────────────────────────────────────────────────────────────
export default function UserManagement() {
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoles, setInviteRoles] = useState<string[]>([]);
  const [inviteOrgType, setInviteOrgType] = useState<'buyer' | 'seller'>('buyer');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ success: boolean; message: string; tempPassword?: string } | null>(null);

  // Role management state
  const [managingUser, setManagingUser] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [roleToAdd, setRoleToAdd] = useState('');
  const [rolesLoading, setRolesLoading] = useState(false);

  // Check admin access
  useEffect(() => {
    authApi.me().then((res: any) => {
      const perms = res?.permissions || res?.user?.permissions || {};
      if (perms.can_admin === true) {
        setAuthorized(true);
      } else {
        setAuthorized(false);
      }
    }).catch(() => setAuthorized(false));
  }, []);

  // Fetch users
  useEffect(() => {
    if (authorized) {
      loadUsers();
    }
  }, [authorized]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await usersApi.list();
      setUsers(res?.users || res?.items || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    }
    setLoading(false);
  };

  // Handle invite user
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail || inviteRoles.length === 0) return;
    setInviteLoading(true);
    setInviteResult(null);
    try {
      const res = await usersApi.create({ email: inviteEmail, roles: inviteRoles, groups: inviteRoles, org_type: inviteOrgType });
      setInviteResult({
        success: true,
        message: `User ${inviteEmail} created successfully as ${inviteOrgType}.`,
        tempPassword: res?.temporary_password || res?.tempPassword || undefined,
      });
      setInviteEmail('');
      setInviteRoles([]);
      setInviteOrgType('buyer');
      loadUsers();
    } catch (err: any) {
      setInviteResult({ success: false, message: err.message || 'Failed to create user' });
    }
    setInviteLoading(false);
  };

  // Role management
  const openRoleManager = async (username: string) => {
    setManagingUser(username);
    setRolesLoading(true);
    try {
      const res = await usersApi.getGroups(username);
      setUserRoles(res?.groups || res?.roles || []);
    } catch {
      setUserRoles([]);
    }
    setRolesLoading(false);
  };

  const removeRole = async (role: string) => {
    if (!managingUser) return;
    const updated = userRoles.filter(r => r !== role);
    try {
      await usersApi.updateGroups(managingUser, { groups: updated });
      setUserRoles(updated);
      loadUsers();
    } catch (err: any) {
      alert('Failed to remove role: ' + err.message);
    }
  };

  const addRole = async () => {
    if (!managingUser || !roleToAdd) return;
    const updated = [...userRoles, roleToAdd];
    try {
      await usersApi.updateGroups(managingUser, { groups: updated });
      setUserRoles(updated);
      setRoleToAdd('');
      loadUsers();
    } catch (err: any) {
      alert('Failed to add role: ' + err.message);
    }
  };

  // Enable / Disable
  const handleDisable = async (username: string) => {
    try {
      await usersApi.disable(username);
      loadUsers();
    } catch (err: any) {
      alert('Failed to disable user: ' + err.message);
    }
  };

  const handleSetOrgType = async (email: string, org_type: 'buyer' | 'seller') => {
    try {
      await usersApi.setOrgType(email, org_type);
      loadUsers();
    } catch (err: any) {
      alert('Failed to set platform type: ' + err.message);
    }
  };

  const handleEnable = async (username: string) => {
    try {
      await usersApi.enable(username);
      loadUsers();
    } catch (err: any) {
      alert('Failed to enable user: ' + err.message);
    }
  };

  // ─── Access Denied ───────────────────────────────────────────────────────────
  if (authorized === false) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <div style={{ ...card, textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Access Denied</h2>
          <p style={{ fontSize: 13, color: '#6b7280' }}>Administrator Only — You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // ─── Loading ─────────────────────────────────────────────────────────────────
  if (authorized === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <p style={{ fontSize: 13, color: '#6b7280' }}>Checking permissions...</p>
      </div>
    );
  }

  // ─── Main ────────────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>User Management</h2>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Manage platform users, roles, and access</p>
      </div>

      {/* ─── Invite User Card ─────────────────────────────────────────────────── */}
      <div style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginTop: 0, marginBottom: 16 }}>Invite New User</h3>
        <form onSubmit={handleInvite}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>
                Email Address
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="user@agency.gov"
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>
                Platform Type
              </label>
              <select value={inviteOrgType} onChange={e => setInviteOrgType(e.target.value as 'buyer' | 'seller')}
                style={{ ...inputStyle, width: 160 }}>
                <option value="buyer">Buyer (government)</option>
                <option value="seller">Seller (vendor)</option>
              </select>
            </div>
            <button type="submit" disabled={inviteLoading || !inviteEmail || inviteRoles.length === 0} style={{
              ...btnPrimary,
              opacity: inviteLoading || !inviteEmail || inviteRoles.length === 0 ? 0.5 : 1,
            }}>
              {inviteLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
              Assign Roles
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ALL_ROLES.map(role => (
                <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={inviteRoles.includes(role)}
                    onChange={e => {
                      if (e.target.checked) setInviteRoles([...inviteRoles, role]);
                      else setInviteRoles(inviteRoles.filter(r => r !== role));
                    }}
                  />
                  {role.replace(/_/g, ' ')}
                </label>
              ))}
            </div>
          </div>
        </form>

        {inviteResult && (
          <div style={{
            marginTop: 12,
            padding: '12px 16px',
            borderRadius: 6,
            background: inviteResult.success ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${inviteResult.success ? '#bbf7d0' : '#fecaca'}`,
          }}>
            <p style={{ fontSize: 13, color: inviteResult.success ? '#166534' : '#991b1b', margin: 0, fontWeight: 500 }}>
              {inviteResult.message}
            </p>
            {inviteResult.tempPassword && (
              <p style={{ fontSize: 12, color: '#166534', marginTop: 6, marginBottom: 0 }}>
                Temporary Password: <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>{inviteResult.tempPassword}</code>
              </p>
            )}
          </div>
        )}
      </div>

      {/* ─── Users Table ──────────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: 0 }}>Platform Users</h3>
          <button onClick={loadUsers} style={btnSecondary}>Refresh</button>
        </div>

        {loading ? (
          <p style={{ fontSize: 13, color: '#6b7280' }}>Loading users...</p>
        ) : error ? (
          <p style={{ fontSize: 13, color: '#dc2626' }}>{error}</p>
        ) : users.length === 0 ? (
          <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No users found</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Platform</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Enabled</th>
                  <th style={thStyle}>Roles</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user: any) => {
                  const username = user.Username || user.username || user.email;
                  const email = user.email || user.Attributes?.find((a: any) => a.Name === 'email')?.Value || username;
                  const status = user.UserStatus || user.status || 'UNKNOWN';
                  const enabled = user.Enabled !== undefined ? user.Enabled : user.enabled !== undefined ? user.enabled : true;
                  const roles = user.groups || user.roles || [];
                  const created = user.UserCreateDate || user.created_at || '';

                  return (
                    <React.Fragment key={username}>
                      <tr>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: 500, color: '#1e293b' }}>{email}</span>
                        </td>
                        <td style={tdStyle}>
                          <select
                            value={user.org_type === 'seller' ? 'seller' : 'buyer'}
                            onChange={e => handleSetOrgType(email, e.target.value as 'buyer' | 'seller')}
                            aria-label={`Platform type for ${email}`}
                            style={{
                              fontSize: 11, fontWeight: 600, padding: '3px 6px', borderRadius: 6,
                              border: '1px solid #e2e8f0',
                              background: user.org_type === 'seller' ? '#eef2ff' : '#f0fdf4',
                              color: user.org_type === 'seller' ? '#4338ca' : '#166534',
                            }}>
                            <option value="buyer">Buyer</option>
                            <option value="seller">Seller</option>
                          </select>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 10,
                            fontSize: 11,
                            fontWeight: 600,
                            background: status === 'CONFIRMED' ? '#dcfce7' : '#fef9c3',
                            color: status === 'CONFIRMED' ? '#166534' : '#a16207',
                          }}>
                            {status}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 12, color: enabled ? '#16a34a' : '#dc2626' }}>
                            {enabled ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {roles.length > 0 ? roles.map((role: string) => {
                              const colors = ROLE_COLORS[role] || { bg: '#f3f4f6', color: '#4b5563' };
                              return (
                                <span key={role} style={{
                                  display: 'inline-block',
                                  padding: '2px 7px',
                                  borderRadius: 10,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  background: colors.bg,
                                  color: colors.color,
                                }}>
                                  {role.replace(/_/g, ' ')}
                                </span>
                              );
                            }) : (
                              <span style={{ fontSize: 11, color: '#9ca3af' }}>None</span>
                            )}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>
                            {created ? new Date(created).toLocaleDateString() : '—'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              onClick={() => managingUser === username ? setManagingUser(null) : openRoleManager(username)}
                              style={btnSecondary}
                            >
                              {managingUser === username ? 'Close' : 'Manage Roles'}
                            </button>
                            {enabled ? (
                              <button onClick={() => handleDisable(username)} style={btnDanger}>
                                Disable
                              </button>
                            ) : (
                              <button onClick={() => handleEnable(username)} style={btnSuccess}>
                                Enable
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Inline role manager */}
                      {managingUser === username && (
                        <tr>
                          <td colSpan={7} style={{ padding: '12px 12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ maxWidth: 600 }}>
                              <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 0, marginBottom: 10 }}>
                                Roles for {email}
                              </p>
                              {rolesLoading ? (
                                <p style={{ fontSize: 12, color: '#6b7280' }}>Loading roles...</p>
                              ) : (
                                <>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                                    {userRoles.length === 0 && (
                                      <span style={{ fontSize: 12, color: '#9ca3af' }}>No roles assigned</span>
                                    )}
                                    {userRoles.map(role => {
                                      const colors = ROLE_COLORS[role] || { bg: '#f3f4f6', color: '#4b5563' };
                                      return (
                                        <span key={role} style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 4,
                                          padding: '3px 8px',
                                          borderRadius: 10,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          background: colors.bg,
                                          color: colors.color,
                                        }}>
                                          {role.replace(/_/g, ' ')}
                                          <button
                                            onClick={() => removeRole(role)}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              cursor: 'pointer',
                                              fontSize: 13,
                                              lineHeight: 1,
                                              color: colors.color,
                                              padding: 0,
                                              marginLeft: 2,
                                            }}
                                            title={`Remove ${role}`}
                                          >
                                            ×
                                          </button>
                                        </span>
                                      );
                                    })}
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <select
                                      value={roleToAdd}
                                      onChange={e => setRoleToAdd(e.target.value)}
                                      style={{ ...inputStyle, width: 220 }}
                                    >
                                      <option value="">Select role to add...</option>
                                      {ALL_ROLES.filter(r => !userRoles.includes(r)).map(role => (
                                        <option key={role} value={role}>{role.replace(/_/g, ' ')}</option>
                                      ))}
                                    </select>
                                    <button
                                      onClick={addRole}
                                      disabled={!roleToAdd}
                                      style={{ ...btnPrimary, opacity: !roleToAdd ? 0.5 : 1, padding: '8px 14px' }}
                                    >
                                      Add Role
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
